import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import adminServiceCategoryService from '../../services/adminServiceCategoryService';
import BackButton from '../../components/BackButton';

const { TextArea } = Input;

const ServiceCategoryList = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    const result = await adminServiceCategoryService.getServiceCategories();
    if (result.success) {
      setCategories(result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name_en: category.name_en,
      name_el: category.name_el,
      description_en: category.description_en,
      description_el: category.description_el,
      is_active: category.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const result = await adminServiceCategoryService.deleteServiceCategory(id);
    if (result.success) {
      message.success(t('admin.serviceCategories.deleteSuccess'));
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.serviceCategories.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.serviceCategories.nameEl'), dataIndex: 'name_el' },
      { title: t('admin.serviceCategories.descriptionEn'), dataIndex: 'description_en' },
      { title: t('admin.serviceCategories.descriptionEl'), dataIndex: 'description_el' },
      {
        title: t('common.status'),
        dataIndex: 'is_active',
        exportRender: (val) => val ? t('common.active') : t('common.inactive')
      }
    ];
    exportToExcel(categories, exportColumns, 'service_categories', t('admin.serviceCategories.title'));
    message.success(t('admin.serviceCategories.exportSuccess'));
  };

  const handleSubmit = async (values) => {
    const result = editingCategory
      ? await adminServiceCategoryService.updateServiceCategory(editingCategory.id, values)
      : await adminServiceCategoryService.createServiceCategory(values);

    if (result.success) {
      message.success(editingCategory ? t('admin.serviceCategories.updateSuccess') : t('admin.serviceCategories.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const columns = [
    {
      title: t('admin.serviceCategories.nameEn'),
      dataIndex: 'name_en',
      key: 'name_en',
      sorter: (a, b) => a.name_en.localeCompare(b.name_en)
    },
    {
      title: t('admin.serviceCategories.nameEl'),
      dataIndex: 'name_el',
      key: 'name_el'
    },
    {
      title: t('admin.serviceCategories.descriptionEn'),
      dataIndex: 'description_en',
      key: 'description_en',
      ellipsis: true,
      width: 250
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
      filters: [
        { text: t('common.active'), value: true },
        { text: t('common.inactive'), value: false }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('admin.serviceCategories.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={t('admin.serviceCategories.title')}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!categories.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('admin.serviceCategories.addCategory')}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('admin.serviceCategories.totalCategories', { total })
          }}
        />
      </Card>

      <Modal
        title={editingCategory ? t('admin.serviceCategories.editCategory') : t('admin.serviceCategories.addCategory')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="name_en"
            label={t('admin.serviceCategories.nameEn')}
            rules={[
              { required: true, message: t('admin.serviceCategories.nameEnRequired') },
              { max: 100, message: t('admin.serviceCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.serviceCategories.nameEnPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('admin.serviceCategories.nameEl')}
            rules={[
              { required: true, message: t('admin.serviceCategories.nameElRequired') },
              { max: 100, message: t('admin.serviceCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.serviceCategories.nameElPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description_en"
            label={t('admin.serviceCategories.descriptionEn')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.serviceCategories.descriptionEnPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="description_el"
            label={t('admin.serviceCategories.descriptionEl')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.serviceCategories.descriptionElPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCategory ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceCategoryList;
